/*
PURPOSE: Provides convenient hooks for checking user access levels and contributor status

USE CASES:
- Components that need to show/hide features based on contributor status
- Route guards that require specific access levels
- UI elements that need contributor-specific styling or behavior

DOMAIN BOUNDARIES:
- Responsible for providing easy access to access control information
- Should NOT handle authentication logic - that's in AuthContext
- Should NOT make API calls - uses existing context data

ASSUMPTIONS:
- AuthContext provides accurate access level information
- Components using these hooks are wrapped in AuthProvider
- Access levels are calculated correctly by backend and stored in user profile
*/

import { useAuthContext } from '../components/Auth/AuthContext/AuthContext';
import { AccessLevel } from 'shared/types/platformTypes';

/**
 * Hook to check if user has contributor access
 * @returns boolean indicating if user is a contributor
 */
export const useIsContributor = (): boolean => {
  const { isContributor } = useAuthContext();
  return isContributor;
};

/**
 * Hook to check if user has at least member access (authenticated)
 * @returns boolean indicating if user is authenticated
 */
export const useIsMember = (): boolean => {
  const { isMember } = useAuthContext();
  return isMember;
};

/**
 * Hook to get the user's current access level
 * @returns AccessLevel ('member' | 'contributor')
 */
export const useAccessLevel = (): AccessLevel => {
  const { accessLevel } = useAuthContext();
  return accessLevel;
};

/**
 * Hook to check if user has a specific access level or higher
 * @param requiredLevel - The minimum access level required
 * @returns boolean indicating if user meets the requirement
 */
export const useHasAccessLevel = (requiredLevel: AccessLevel): boolean => {
  const { accessLevel } = useAuthContext();
  
  // Access level hierarchy: member < contributor
  const levelHierarchy = { member: 1, contributor: 2 };
  
  return levelHierarchy[accessLevel] >= levelHierarchy[requiredLevel];
};

/**
 * Hook to get contributor type information
 * @returns ContributorType ('coral_subscriber' | 'paid_artist' | 'both' | null)
 */
export const useContributorType = () => {
  const { contributorType } = useAuthContext();
  return contributorType;
};

/**
 * Hook to check if user is a coral subscriber
 * @returns boolean indicating if user has active coral subscriptions
 */
export const useIsCoralSubscriber = (): boolean => {
  const { contributorType } = useAuthContext();
  return contributorType === 'coral_subscriber' || contributorType === 'both';
};

/**
 * Hook to check if user is a paid artist
 * @returns boolean indicating if user is a verified artist with payments
 */
export const useIsPaidArtist = (): boolean => {
  const { contributorType } = useAuthContext();
  return contributorType === 'paid_artist' || contributorType === 'both';
};